import { PracticeDetailTypes, ProgramTaskRole, RecommendationTag } from '@cibo/core'
import { LineLimitTypography, ResponsiveDialog } from '@cibo/ui'
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline'
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline'
import StarRateIcon from '@mui/icons-material/StarRate'
import {
  Box,
  Card,
  Checkbox,
  Chip,
  DialogActions,
  DialogContent,
  FormControlLabel,
  Stack,
  alpha,
  styled,
  useTheme,
} from '@mui/material'
import Button from '@mui/material/Button'
import CardContent from '@mui/material/CardContent'
import Typography from '@mui/material/Typography'
import { ChangeEvent, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { PracticeCardDrawer } from './PracticeCardDrawer'

type PracticeCardProps = {
  title?: string
  subTitle?: string
  recommendedTag?: RecommendationTag[]
  practiceDetails?: PracticeDetailTypes
  resourceConcernList?: string[]
  userRole?: ProgramTaskRole
  ownerName?: string
  isSelected: boolean
  onClickSelect: (practiceId?: string) => void
}

const SelectableCard = ({
  isSelected,
  eligibility,
  ...cardProps
}: PracticeCardProps & {
  isSelected: boolean
  eligibility: boolean
}) => <Card {...cardProps} />

const StyledCard = styled(SelectableCard)(({ theme, isSelected, eligibility }) =>
  eligibility
    ? {
        border: isSelected ? `1px solid ${theme.palette.secondary.main}` : undefined,
        backgroundColor: isSelected ? alpha(theme.palette.secondary.main, 0.08) : undefined,
      }
    : {
        backgroundColor: alpha(theme.palette.action.disabledBackground, 0.12),
      }
)

export const PracticeCard = ({
  onClickSelect,
  recommendedTag,
  isSelected,
  practiceDetails,
  title,
  subTitle,
  ...rest
}: PracticeCardProps) => {
  const { palette } = useTheme()
  const { t } = useTranslation('@cibo/landmanager/PracticeCards')

  const isEligible = !recommendedTag?.includes('Ineligible')
  const recommended = recommendedTag?.includes('Highly Recommended')
  const isStatePriority = recommendedTag?.includes('Add-On')

  const cardButton =
    isSelected || !isEligible ? (
      <Button
        size="small"
        variant="outlined"
        sx={{
          color: palette.text.secondary,
          borderColor: palette.text.primary,
        }}
        startIcon={<RemoveCircleOutlineIcon />}
        onClick={() => {
          onClickSelect(practiceDetails?.practiceId)
        }}
        disabled={!isEligible}
      >
        {isSelected ? t('removePractice') : t('addPractice')}
      </Button>
    ) : (
      <Button
        size="small"
        variant="outlined"
        color="secondary"
        startIcon={<AddCircleOutlineIcon />}
        onClick={() => {
          onClickSelect(practiceDetails?.practiceId)
        }}
      >
        {t('addPractice')}
      </Button>
    )

  const [dialogOpen, setDialogOpen] = useState(false)
  const [isChecked, setIsChecked] = useState(false)

  const handleOpenDialog = () => {
    setDialogOpen(true)
  }

  const handleCloseDialog = () => {
    setDialogOpen(false)
  }

  const handleCheckBox = (event: ChangeEvent<HTMLInputElement>) => {
    setIsChecked(event.target.checked)
  }

  return (
    // @ts-ignore passing `isSelected` props to StyledCard
    <StyledCard variant="outlined" isSelected={isSelected} eligibility={isEligible}>
      <CardContent sx={{ padding: 3 }}>
        <Stack direction="column" spacing={1.5}>
          <Stack
            direction="row"
            sx={{
              justifyContent: 'space-between',
              display: 'flex',
            }}
          >
            <Typography component="div" textOverflow="ellipsis" noWrap>
              {title}
            </Typography>
            <Stack
              direction="row"
              spacing={1}
              sx={{
                alignItems: 'center',
              }}
            >
              {isStatePriority && <StarRateIcon fontSize="medium" color="info" />}
              {!isEligible ? (
                <Chip label={t('ineligible')} color="error" size="small" />
              ) : (
                recommended && <Chip label={t('recommended')} color="success" size="small" />
              )}
            </Stack>
          </Stack>

          <LineLimitTypography
            lineLimit={2}
            minHeight="40px"
            textOverflow="ellipsis"
            variant="body2"
            color="text.secondary"
          >
            {subTitle}
          </LineLimitTypography>
          <Stack direction="row" spacing={1.5}>
            {isSelected || !isEligible ? (
              <Button
                size="small"
                variant="outlined"
                sx={{
                  color: palette.text.secondary,
                  borderColor: palette.text.primary,
                }}
                startIcon={<RemoveCircleOutlineIcon />}
                onClick={() => {
                  onClickSelect(practiceDetails?.practiceId)
                }}
                disabled={!isEligible}
              >
                {isSelected ? t('removePractice') : t('addPractice')}
              </Button>
            ) : (
              <Button
                size="small"
                variant="outlined"
                color="secondary"
                startIcon={<AddCircleOutlineIcon />}
                onClick={() => {
                  if (practiceDetails?.requirementText) {
                    handleOpenDialog()
                  } else {
                    onClickSelect(practiceDetails?.practiceId)
                  }
                }}
              >
                {t('addPractice')}
              </Button>
            )}
            {practiceDetails?.requirementText && (
              <ResponsiveDialog
                open={dialogOpen}
                onClose={handleCloseDialog}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <DialogContent>
                  <Stack spacing={3}>
                    <Typography variant="h6" component="h2">
                      {t('dialogTitle')}
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                      {t('dialogSubtitle')}
                    </Typography>
                    <Box
                      padding={1.5}
                      sx={{
                        backgroundColor: 'background.default',
                        overflowY: 'auto',
                      }}
                    >
                      <Typography variant="body1">{practiceDetails?.requirementText}</Typography>
                    </Box>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={isChecked}
                          onChange={handleCheckBox}
                          name="confirmCheckbox"
                        />
                      }
                      label={t('confirmCheckboxLabel')}
                    />
                    <DialogActions>
                      <Stack spacing={3} direction="row">
                        <Button color="secondary" onClick={handleCloseDialog}>
                          {t('cancel')}
                        </Button>
                        <Button
                          color="primary"
                          onClick={() => {
                            onClickSelect(practiceDetails?.practiceId)
                            handleCloseDialog()
                          }}
                          variant="contained"
                          disabled={!isChecked}
                        >
                          {t('addPractice')}
                        </Button>
                      </Stack>
                    </DialogActions>
                  </Stack>
                </DialogContent>
              </ResponsiveDialog>
            )}
            <PracticeCardDrawer
              practiceName={title}
              practiceDetails={practiceDetails}
              eligibility={isEligible}
              cardButton={cardButton}
              {...rest}
            />
          </Stack>
        </Stack>
      </CardContent>
    </StyledCard>
  )
}
